import React, { useState } from "react";
import { Loader2, Check } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

const IMG = "/affiche-sur-mesure.jpg";

const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-sm text-foreground/70 mb-2 block">{label}</span>
    {children}
  </label>
);

const inputCls = "w-full bg-muted/50 border border-border rounded-xl px-4 py-3 focus:ring-2 focus:ring-primary/50 focus:border-primary outline-none transition-colors";

const Contact = () => {
  const [order, setOrder] = useState({ name: "", email: "", phone: "", condition: "Alzheimer", memories: "", needs: "" });
  const [contact, setContact] = useState({ name: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submitOrder = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/custom-order", order);
      setDone(true);
      toast.success("Votre demande a bien été envoyée !");
      setOrder({ name: "", email: "", phone: "", condition: "Alzheimer", memories: "", needs: "" });
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    setLoading(false);
  };
  const submitContact = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", contact);
      toast.success("Message envoyé, merci !");
      setContact({ name: "", email: "", message: "" });
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    setLoading(false);
  };

  return (
    <div className="pt-24">
      <div className="grid lg:grid-cols-2 min-h-screen">
        <div className="relative hidden lg:block">
          <img src={IMG} alt="Accompagnement bienveillant" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-foreground/25" />
          <div className="absolute bottom-0 p-14">
            <h2 className="font-serif-display text-5xl text-background font-light leading-tight max-w-md">Parlons de la personne à accompagner.</h2>
            <p className="text-background/80 mt-5 max-w-sm leading-relaxed">Nous lisons chaque demande avec attention et vous répondons personnellement sous 48h.</p>
          </div>
        </div>

        <div className="px-6 sm:px-10 lg:px-16 py-16">
          <Reveal>
            <p className="text-sm uppercase tracking-[0.3em] text-primary mb-4">Écrivons ensemble</p>
            <h1 className="font-serif-display text-4xl sm:text-5xl font-light">Une demande sur mesure</h1>
          </Reveal>

          <Tabs defaultValue="order" className="mt-10">
            <TabsList className="bg-muted/60 rounded-full p-1">
              <TabsTrigger value="order" data-testid="tab-order" className="rounded-full px-6 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">Demande personnalisée</TabsTrigger>
              <TabsTrigger value="contact" data-testid="tab-contact" className="rounded-full px-6 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">Contact simple</TabsTrigger>
            </TabsList>

            <TabsContent value="order" className="mt-8">
              {done ? (
                <div className="bg-secondary/20 border border-secondary/40 rounded-3xl p-10 text-center" data-testid="order-success">
                  <div className="w-14 h-14 rounded-full bg-secondary/40 flex items-center justify-center mx-auto"><Check className="w-7 h-7 text-secondary-foreground" /></div>
                  <h3 className="font-serif-display text-2xl mt-5">Merci pour votre confiance</h3>
                  <p className="text-foreground/70 mt-3">Votre demande nous est parvenue. Nous revenons vers vous très vite.</p>
                  <button onClick={() => setDone(false)} className="mt-6 text-primary underline">Envoyer une autre demande</button>
                </div>
              ) : (
                <form onSubmit={submitOrder} className="space-y-5" data-testid="custom-order-form">
                  <div className="grid sm:grid-cols-2 gap-5">
                    <Field label="Votre nom"><input required className={inputCls} data-testid="order-name" value={order.name} onChange={(e) => setOrder({ ...order, name: e.target.value })} /></Field>
                    <Field label="Email"><input required type="email" className={inputCls} data-testid="order-email" value={order.email} onChange={(e) => setOrder({ ...order, email: e.target.value })} /></Field>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-5">
                    <Field label="Téléphone (optionnel)"><input className={inputCls} data-testid="order-phone" value={order.phone} onChange={(e) => setOrder({ ...order, phone: e.target.value })} /></Field>
                    <Field label="Pathologie / contexte">
                      <select className={inputCls} data-testid="order-condition" value={order.condition} onChange={(e) => setOrder({ ...order, condition: e.target.value })}>
                        <option>Alzheimer</option><option>Autisme</option><option>Maladie dégénérative</option><option>Particulier</option>
                      </select>
                    </Field>
                  </div>
                  <Field label="Souvenirs, goûts ou éléments importants"><textarea required rows={3} className={inputCls} data-testid="order-memories" value={order.memories} onChange={(e) => setOrder({ ...order, memories: e.target.value })} placeholder="Ex : la mer, le jardin, une chanson, une couleur..." /></Field>
                  <Field label="Besoins et attentes"><textarea required rows={3} className={inputCls} data-testid="order-needs" value={order.needs} onChange={(e) => setOrder({ ...order, needs: e.target.value })} placeholder="Décrivez ce que vous imaginez, l'usage souhaité..." /></Field>
                  <button disabled={loading} data-testid="submit-order-btn" className="rounded-full bg-primary text-primary-foreground px-9 py-4 flex items-center gap-2 hover:scale-[1.02] transition-transform duration-300 disabled:opacity-60">
                    {loading && <Loader2 className="w-4 h-4 animate-spin" />} Envoyer ma demande
                  </button>
                </form>
              )}
            </TabsContent>

            <TabsContent value="contact" className="mt-8">
              <form onSubmit={submitContact} className="space-y-5" data-testid="contact-form">
                <Field label="Votre nom"><input required className={inputCls} data-testid="contact-name" value={contact.name} onChange={(e) => setContact({ ...contact, name: e.target.value })} /></Field>
                <Field label="Email"><input required type="email" className={inputCls} data-testid="contact-email" value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} /></Field>
                <Field label="Message"><textarea required rows={5} className={inputCls} data-testid="contact-message" value={contact.message} onChange={(e) => setContact({ ...contact, message: e.target.value })} /></Field>
                <button disabled={loading} data-testid="submit-contact-btn" className="rounded-full bg-primary text-primary-foreground px-9 py-4 flex items-center gap-2 hover:scale-[1.02] transition-transform duration-300 disabled:opacity-60">
                  {loading && <Loader2 className="w-4 h-4 animate-spin" />} Envoyer le message
                </button>
              </form>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
};

export default Contact;
